import React, { useState, useEffect } from 'react';
import {
    Box, Button, TextField, Table, TableBody, TableCell, TableContainer,
    TableHead, TableRow, Paper, Typography, IconButton, Dialog,
    DialogTitle, DialogContent, DialogActions, InputAdornment,
    Grid, Autocomplete, MenuItem, Avatar
} from '@mui/material';
import { Add, Edit, Delete, Print, Search, AccountCircle } from '@mui/icons-material';
import { collection, addDoc, onSnapshot, query, orderBy, deleteDoc, doc, updateDoc, getDocs } from "firebase/firestore";
import { db } from "../../firebase";
import Swal from 'sweetalert2';

function Studentoutpass() {
    const [outPasses, setOutPasses] = useState([]);
    const [studentList, setStudentList] = useState([]);
    const [search, setSearch] = useState('');
    const [open, setOpen] = useState(false);
    const [editId, setEditId] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);

    const [formData, setFormData] = useState({
        studentId: '', studentName: '', className: '', photo: '', staffType: 'Parent',
        staffName: '', date: new Date().toLocaleString(), reason: '',
        durationValue: '', durationType: 'Hours', returnDate: new Date().toLocaleDateString(), contactNo: ''
    });

    const [printData, setPrintData] = useState(null);

    // --- NEW: Native Print Logic ---
    const triggerPrint = (item) => {
        setPrintData(item);
        // Timeout ensures React renders the printData into the hidden div 
        // before the print dialog freezes the browser execution.
        setTimeout(() => {
            window.print();
        }, 500);
    };

    // 1. Fetch Student/Admission/Course Data (Keep as is)
    useEffect(() => {
        const fetchStudents = async () => {
            try {
                const admissionsSnap = await getDocs(collection(db, "admission"));
                const studentsSnap = await getDocs(collection(db, "students"));
                const coursesSnap = await getDocs(collection(db, "courses"));
                const studentsMap = {};
                studentsSnap.forEach(doc => { studentsMap[doc.id] = doc.data(); });
                const coursesMap = {};
                coursesSnap.forEach(doc => {
                    coursesMap[doc.id] = doc.data().courseName || doc.data().name || 'N/A';
                });
                const combined = admissionsSnap.docs.map(admDoc => {
                    const adm = admDoc.data();
                    const profile = studentsMap[adm.studentId] || {};
                    return {
                        id: adm.studentId,
                        fullName: `${profile.firstname || ''} ${profile.fathername || ''} ${profile.surname || ''}`.trim(),
                        class: coursesMap[adm.class] || 'N/A',
                        photo: profile.photoURL || profile.studentPhoto || '',
                        contact: adm.father_mobile1 || ''
                    };
                });
                setStudentList(combined);
            } catch (error) { console.error("Error fetching students:", error); }
        };
        fetchStudents();
    }, []);

    // 2. Real-time Out Pass Sync (Keep as is)
    useEffect(() => {
        const q = query(collection(db, "out_passes"), orderBy("srNo", "desc"));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            setOutPasses(snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
        });
        return () => unsubscribe();
    }, []);

    const handleSave = async () => {
        try {
            if (editId) {
                await updateDoc(doc(db, "out_passes", editId), formData);
            } else {
                const nextSrNo = outPasses.length > 0 ? (Math.max(...outPasses.map(o => o.srNo || 0)) + 1) : 1;
                await addDoc(collection(db, "out_passes"), { ...formData, srNo: nextSrNo, timestamp: new Date() });
            }
            setOpen(false); setEditId(null); resetForm();
            Swal.fire({ title: "Success", timer: 1500, icon: "success", showConfirmButton: false });
        } catch (e) { Swal.fire("Error", e.message, "error"); }
    };

    const resetForm = () => setFormData({
        studentId: '', studentName: '', className: '', photo: '', staffType: 'Parent',
        staffName: '', date: new Date().toLocaleString(), reason: '',
        durationValue: '', durationType: 'Hours', returnDate: new Date().toLocaleDateString(), contactNo: ''
    });

    const filtered = outPasses.filter(p => p.studentName?.toLowerCase().includes(search.toLowerCase()));

    return (
        <Box p={1} sx={{ bgcolor: '#f8f9fa', minHeight: '100vh' }}>
            {/* --- CRITICAL: CSS FOR WINDOW.PRINT --- */}
            <style>
                {`
                @media print {
                    /* Hide the dashboard, buttons, and tables */
                    body * { visibility: hidden; }
                    /* Show only the print template */
                    #printable-area, #printable-area * { visibility: visible; }
                    #printable-area {
                        position: absolute;
                        left: 0;
                        top: 0;
                        width: 100%;
                        display: block !important;
                    }
                    /* Remove default browser margins/headers */
                    @page { margin: 10mm; }
                }
                `}
            </style>

            <Box className="no-print">
                <Typography variant="h6" fontWeight="900" color="primary">Student Out Pass</Typography>
                <Paper sx={{ p: 1, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <TextField
                        size="small" placeholder="Search..."
                        onChange={(e) => setSearch(e.target.value)}
                        InputProps={{ startAdornment: <Search sx={{ fontSize: 20, mr: 1, color: 'gray' }} /> }}
                        sx={{ width: 250 }}
                    />
                    <Button size="small" variant="contained" color="error" startIcon={<Add />} onClick={() => { resetForm(); setOpen(true); }}>
                        New Out Pass
                    </Button>
                </Paper>

                <TableContainer component={Paper} sx={{ maxHeight: '75vh' }}>
                    <Table size="small" stickyHeader>
                        <TableHead>
                            <TableRow sx={{ bgcolor: '#f0f0f0' }}>
                                <TableCell sx={{ fontWeight: 'bold' }}>No.</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Student</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Class</TableCell>
                                <TableCell sx={{ fontWeight: 'bold' }}>Out Date/Time</TableCell>
                                <TableCell align="right" sx={{ fontWeight: 'bold' }}>Actions</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {filtered.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage).map((item) => (
                                <TableRow key={item.id} hover>
                                    <TableCell>{item.srNo}</TableCell>
                                    <TableCell>
                                        <Box display="flex" alignItems="center" gap={1}>
                                            <Avatar src={item.photo} sx={{ width: 24, height: 24 }}><AccountCircle /></Avatar>
                                            {item.studentName}
                                        </Box>
                                    </TableCell>
                                    <TableCell>{item.className}</TableCell>
                                    <TableCell sx={{ fontSize: '0.8rem' }}>{item.date}</TableCell>
                                    <TableCell align="right">
                                        <IconButton size="small" color="primary" onClick={() => triggerPrint(item)}><Print fontSize="small" /></IconButton>
                                        <IconButton size="small" onClick={() => { setEditId(item.id); setFormData(item); setOpen(true); }}><Edit fontSize="small" /></IconButton>
                                        <IconButton size="small" color="error" onClick={async () => {
                                            const res = await Swal.fire({ title: 'Delete?', showCancelButton: true });
                                            if (res.isConfirmed) await deleteDoc(doc(db, "out_passes", item.id));
                                        }}><Delete fontSize="small" /></IconButton>
                                    </TableCell>
                                </TableRow>
                            ))}
                        </TableBody>
                    </Table>
                </TableContainer>
            </Box>

            {/* --- Keep your Add/Edit Dialog as is --- */}
            <Dialog open={open} onClose={() => setOpen(false)} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ bgcolor: '#b71c1c', color: 'white', mb: 2, py: 1.5 }}>
                    {editId ? 'Modify Out Pass' : 'New Out Pass Registration'}
                </DialogTitle>
                <DialogContent>
                    <Grid container spacing={2} sx={{ mt: 0.5 }}>
                        <Grid item xs={12}>
                            <Autocomplete
                                size="small"
                                options={studentList}
                                getOptionLabel={(option) => option.fullName || ""}
                                onChange={(e, val) => {
                                    if (val) setFormData({
                                        ...formData,
                                        studentId: val.id, studentName: val.fullName,
                                        className: val.class, photo: val.photo, contactNo: val.contact
                                    });
                                }}
                                renderInput={(params) => <TextField {...params} label="Select Student" fullWidth />}
                            />
                        </Grid>
                        {formData.studentName && (
                            <Grid item xs={12}>
                                <Box display="flex" alignItems="center" gap={2} p={1} bgcolor="#fff5f5" borderRadius={1} border="1px solid #ffcdd2">
                                    <Avatar src={formData.photo} sx={{ width: 50, height: 50 }} />
                                    <Box>
                                        <Typography variant="subtitle2"><b>{formData.studentName}</b></Typography>
                                        <Typography variant="caption" color="textSecondary">Class: {formData.className} | Mobile: {formData.contactNo}</Typography>
                                    </Box>
                                </Box>
                            </Grid>
                        )}
                        <Grid item xs={6}><TextField size="small" label="Out Date & Time" fullWidth value={formData.date} onChange={e => setFormData({ ...formData, date: e.target.value })} /></Grid>
                        <Grid item xs={6}>
                            <TextField select size="small" label="With Person" fullWidth value={formData.staffType} onChange={e => setFormData({ ...formData, staffType: e.target.value })}>
                                <MenuItem value="Parent">Parent</MenuItem>
                                <MenuItem value="Guardian">Guardian</MenuItem>
                                <MenuItem value="Staff">Staff</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={12}><TextField size="small" label="Name of Parent/Staff" fullWidth value={formData.staffName} onChange={e => setFormData({ ...formData, staffName: e.target.value })} /></Grid>
                        <Grid item xs={12}><TextField size="small" label="Reason" fullWidth value={formData.reason} onChange={e => setFormData({ ...formData, reason: e.target.value })} /></Grid>
                        <Grid item xs={4}><TextField size="small" label="Duration" fullWidth value={formData.durationValue} onChange={e => setFormData({ ...formData, durationValue: e.target.value })} /></Grid>
                        <Grid item xs={4}>
                            <TextField select size="small" fullWidth value={formData.durationType} onChange={e => setFormData({ ...formData, durationType: e.target.value })}>
                                <MenuItem value="Hours">Hours</MenuItem>
                                <MenuItem value="Days">Days</MenuItem>
                            </TextField>
                        </Grid>
                        <Grid item xs={4}><TextField size="small" label="Return Date/Time" fullWidth value={formData.returnDate} onChange={e => setFormData({ ...formData, returnDate: e.target.value })} /></Grid>
                    </Grid>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpen(false)}>Cancel</Button>
                    <Button variant="contained" color="error" onClick={handleSave}>Save Pass</Button>
                </DialogActions>
            </Dialog>

            {/* --- PRINT TEMPLATE (Hidden from UI, Visible to window.print) --- */}
            <div id="printable-area" style={{ display: 'none' }}>
                {printData && (
                    <div id="printable-area" style={{
                        width: '310mm',
                        minHeight: '148mm', // Half A4 size (A5 landscape) looks great for passes
                        padding: '20px',
                        backgroundColor: 'white',
                        margin: '0 auto'
                    }}>
                        <div style={{
                            border: '4px double #000',
                            padding: '20px',
                            position: 'relative',
                            borderRadius: '8px'
                        }}>
                            {/* --- WATERMARK EFFECT (Optional) --- */}
                            <div style={{
                                position: 'absolute',
                                top: '50%',
                                left: '50%',
                                transform: 'translate(-50%, -50%) rotate(-30deg)',
                                fontSize: '8rem',
                                color: 'rgba(0,0,0,0.03)',
                                fontWeight: 'bold',
                                zIndex: 0,
                                whiteSpace: 'nowrap',
                                pointerEvents: 'none'
                            }}>
                                MMIS PASS
                            </div>

                            <div style={{ position: 'relative', zIndex: 1 }}>
                                {/* --- HEADER --- */}
                                <Box textAlign="center" borderBottom="2px solid #000" pb={1} mb={2}>
                                    <Typography variant="h4" sx={{ fontWeight: 900, letterSpacing: 1, color: '#1a237e' }}>
                                        MOHAN MALI INTERNATIONAL SCHOOL
                                    </Typography>
                                    <Typography variant="body2" sx={{ fontStyle: 'italic', fontWeight: 500 }}>
                                        Near Kavathe Mahankal Railway Station, Alkud (S), Kavathe Mahankal.
                                    </Typography>
                                </Box>

                                {/* --- TITLE BAR --- */}
                                <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
                                    <Typography variant="h6"><b>No:</b> <span style={{ color: '#d32f2f' }}>{printData.srNo}</span></Typography>
                                    <Box sx={{
                                        bgcolor: '#000',
                                        color: '#fff',
                                        px: 4,
                                        py: 0.5,
                                        borderRadius: '4px',
                                        fontWeight: 'bold',
                                        fontSize: '1.2rem',
                                        textTransform: 'uppercase'
                                    }}>
                                        Student Out Pass
                                    </Box>
                                    <Typography variant="h6"><b>Date:</b> {printData.date?.split(',')[0]}</Typography>
                                </Box>

                                {/* --- CONTENT TABLE STYLE --- */}
                                <div style={{
                                    display: 'grid',
                                    gridTemplateColumns: '1fr 1fr',
                                    gap: '15px',
                                    fontSize: '1.1rem',
                                    lineHeight: '1.6'
                                }}>
                                    <div style={{ gridColumn: 'span 2', borderBottom: '1px dashed #ccc', paddingBottom: '5px' }}>
                                        <span>Student Name:</span> <b style={{ fontSize: '1.3rem', marginLeft: '10px' }}>{printData.studentName.toUpperCase()}</b>
                                    </div>

                                    <div style={{ borderBottom: '1px dashed #ccc' }}>
                                        <span>Class:</span> <b style={{ marginLeft: '10px' }}>{printData.className}</b>
                                    </div>

                                    <div style={{ borderBottom: '1px dashed #ccc' }}>
                                        <span>Contact No:</span> <b style={{ marginLeft: '10px' }}>{printData.contactNo}</b>
                                    </div>

                                    <div style={{ borderBottom: '1px dashed #ccc' }}>
                                        <span>Accompanied By:</span> <b style={{ marginLeft: '10px' }}>{printData.staffName} ({printData.staffType})</b>
                                    </div>

                                    <div style={{ borderBottom: '1px dashed #ccc' }}>
                                        <span>Out Time:</span> <b style={{ marginLeft: '10px' }}>{printData.date?.split(',')[1] || printData.date}</b>
                                    </div>

                                    <div style={{ gridColumn: 'span 2', borderBottom: '1px dashed #ccc' }}>
                                        <span>Reason:</span> <b style={{ marginLeft: '10px' }}>{printData.reason}</b>
                                    </div>

                                    <div style={{ borderBottom: '1px dashed #ccc' }}>
                                        <span>Duration:</span> <b style={{ marginLeft: '10px' }}>{printData.durationValue} {printData.durationType}</b>
                                    </div>

                                    <div style={{ borderBottom: '1px dashed #ccc' }}>
                                        <span>Expected Return:</span> <b style={{ marginLeft: '10px' }}>{printData.returnDate}</b>
                                    </div>
                                </div>

                                {/* --- SIGNATURE SECTION --- */}
                                <Box display="flex" justifyContent="space-between" mt={8}>
                                    <Box textAlign="center">
                                        <div style={{ width: '160px', borderTop: '1.5px solid #000', marginTop: '10px' }}></div>
                                        <Typography variant="caption" fontWeight="bold">Parent/Guardian</Typography>
                                    </Box>
                                    <Box textAlign="center">
                                        <div style={{ width: '160px', borderTop: '1.5px solid #000', marginTop: '10px' }}></div>
                                        <Typography variant="caption" fontWeight="bold">Class Teacher</Typography>
                                    </Box>
                                    <Box textAlign="center">
                                        <div style={{ width: '160px', borderTop: '1.5px solid #000', marginTop: '10px' }}></div>
                                        <Typography variant="caption" fontWeight="bold">Principal/Office</Typography>
                                    </Box>
                                </Box>
                            </div>
                        </div>

                        {/* Helper text for office use (cut line) */}
                        <Typography variant="caption" sx={{ display: 'block', textAlign: 'center', mt: 1, color: 'gray', fontStyle: 'italic' }}>
                            * This pass must be surrendered at the security gate upon exit.
                        </Typography>
                    </div>
                )}
            </div>
        </Box>
    );
}

export default Studentoutpass;